import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { recruiterApi, type InviteToken, type RecruitedUser, type RecruiterPlan, type RecruiterTrial } from '@/services/recruiterApi';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Humanize a joinedAt ISO → "N days/months" (locale-agnostic count via i18n where needed). */
function tenure(iso: string, now: Date): string {
  const days = Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000));
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

export function Users(): JSX.Element {
  const { t } = useTranslation();
  const [trials, setTrials] = useState<RecruiterTrial[]>([]);
  const [plans, setPlans] = useState<RecruiterPlan[]>([]);
  const [tokens, setTokens] = useState<InviteToken[]>([]);
  const [users, setUsers] = useState<RecruitedUser[]>([]);
  const [status, setStatus] = useState<LoadStatus>('idle');

  const [trialId, setTrialId] = useState('');
  const [subset, setSubset] = useState<string[]>([]);
  const [genError, setGenError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [changeFor, setChangeFor] = useState<RecruitedUser | null>(null);
  const [changePlanId, setChangePlanId] = useState('');

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const [tr, pl, tk, us] = await Promise.all([recruiterApi.listTrials(false), recruiterApi.listPlans(false), recruiterApi.listTokens(), recruiterApi.listUsers()]);
      setTrials(tr.trials); setPlans(pl.plans); setTokens(tk.tokens); setUsers(us.users); setStatus('ready');
    } catch { setStatus('error'); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  async function handleGenerate(): Promise<void> {
    setGenError(null); setNotice(null);
    if (!trialId) { setGenError(t('recruitment.tokens.needsTrial')); return; }
    setBusy(true);
    try {
      const { token } = await recruiterApi.mintToken({ trialId, allowedPlanIds: subset.length ? subset : undefined });
      setNotice(t('recruitment.tokens.created', { code: token.code }));
      setTrialId(''); setSubset([]);
      const tk = await recruiterApi.listTokens();
      setTokens(tk.tokens);
    } catch (err) { setGenError(err instanceof Error ? err.message : t('recruitment.common.actionFailed')); }
    finally { setBusy(false); }
  }

  async function guarded(fn: () => Promise<void>): Promise<void> {
    setBusy(true); setGenError(null);
    try { await fn(); await refresh(); }
    catch (err) { setGenError(err instanceof Error ? err.message : t('recruitment.common.actionFailed')); }
    finally { setBusy(false); }
  }

  const now = new Date();

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('recruitment.users.title')}</h1>
        <p className="muted">{t('recruitment.users.intro')}</p>
      </header>

      {/* Token generator */}
      <section className="admin-card">
        <h2 className="admin-card__title">{t('recruitment.tokens.title')}</h2>
        <p className="muted">{t('recruitment.tokens.intro')}</p>
        <div className="field">
          <label htmlFor="tok-trial">{t('recruitment.tokens.trial')}</label>
          <select id="tok-trial" value={trialId} onChange={(e) => setTrialId(e.target.value)}>
            <option value="">{t('recruitment.tokens.trialPlaceholder')}</option>
            {trials.map((tr) => <option key={tr.id} value={tr.id}>{tr.name}</option>)}
          </select>
        </div>
        <div className="field">
          <span>{t('recruitment.tokens.plansSubset')}</span>
          {plans.map((p) => (
            <label key={p.id} className="checkbox-field">
              <input
                type="checkbox"
                checked={subset.includes(p.id)}
                aria-label={p.name}
                onChange={(e) => setSubset((s) => (e.target.checked ? [...s, p.id] : s.filter((x) => x !== p.id)))}
              />
              {p.name}
            </label>
          ))}
        </div>
        <button type="button" className="btn" disabled={busy} onClick={() => void handleGenerate()}>{t('recruitment.tokens.generate')}</button>
        {genError && <p className="error" role="alert">{genError}</p>}
        {notice && <p className="success" role="status">{notice}</p>}
      </section>

      {/* Outstanding tokens */}
      <section className="admin-card">
        <h2 className="admin-card__title">{t('recruitment.tokens.outstanding')}</h2>
        {tokens.length === 0 ? (
          <p className="muted">{t('recruitment.tokens.empty')}</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('recruitment.tokens.table.code')}</th>
                  <th scope="col">{t('recruitment.tokens.table.trial')}</th>
                  <th scope="col">{t('recruitment.tokens.table.status')}</th>
                  <th scope="col">{t('recruitment.tokens.table.redeemer')}</th>
                  <th scope="col" aria-label={t('recruitment.tokens.table.actions')} />
                </tr>
              </thead>
              <tbody>
                {tokens.map((tk) => (
                  <tr key={tk.id}>
                    <td>
                      <code>{tk.code}</code>
                      <button type="button" className="btn-ghost" onClick={() => void navigator.clipboard?.writeText(tk.code)}>{t('recruitment.common.copy')}</button>
                    </td>
                    <td>{tk.trialName ?? t('recruitment.common.none')}</td>
                    <td>{tk.redeemed ? t('recruitment.tokens.redeemed') : t('recruitment.tokens.unredeemed')}</td>
                    <td>{tk.redeemer ? (tk.redeemer.displayName ?? tk.redeemer.email) : t('recruitment.common.none')}</td>
                    <td className="actions">
                      {!tk.redeemed && (
                        <button type="button" className="btn btn-outline" disabled={busy}
                          onClick={() => { if (window.confirm(t('recruitment.tokens.confirmRevoke'))) void guarded(() => recruiterApi.revokeToken(tk.id).then(() => undefined)); }}>
                          {t('recruitment.tokens.revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Recruited users */}
      <section className="admin-card">
        <h2 className="admin-card__title">{t('recruitment.users.title')}</h2>
        {status === 'loading' && <p className="muted" role="status">{t('recruitment.common.loading')}</p>}
        {status === 'ready' && users.length === 0 && <p className="muted">{t('recruitment.users.empty')}</p>}
        {status === 'ready' && users.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th scope="col">{t('recruitment.users.table.user')}</th>
                  <th scope="col">{t('recruitment.users.table.plans')}</th>
                  <th scope="col">{t('recruitment.users.table.trial')}</th>
                  <th scope="col">{t('recruitment.users.table.tenure')}</th>
                  <th scope="col">{t('recruitment.users.table.lastSeen')}</th>
                  <th scope="col" aria-label={t('recruitment.users.table.actions')} />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const firstHolding = u.plans[0];
                  return (
                    <tr key={u.id}>
                      <td>{u.displayName ?? u.email}<div className="text-dim">{u.email}</div></td>
                      <td>{u.plans.length ? u.plans.map((p) => p.planName ?? p.planId).join(', ') : t('recruitment.users.noPlans')}</td>
                      <td>
                        {u.trial
                          ? u.trial.active
                            ? t('recruitment.users.trialActive', { name: u.trial.trialName ?? '' })
                            : t('recruitment.users.trialExpired', { name: u.trial.trialName ?? '' })
                          : t('recruitment.users.noTrial')}
                      </td>
                      <td>{tenure(u.joinedAt, now)}</td>
                      <td>{u.lastSeen ? new Date(u.lastSeen).toLocaleDateString() : t('recruitment.users.never')}</td>
                      <td className="actions">
                        <button type="button" className="btn-ghost" onClick={() => { setChangeFor(u); setChangePlanId(''); }}>{t('recruitment.users.changePlan')}</button>
                        {firstHolding && (
                          <button type="button" className="btn-ghost" disabled={busy}
                            onClick={() => { if (window.confirm(t('recruitment.users.confirmCancel', { user: u.displayName ?? u.email }))) void guarded(() => recruiterApi.cancelPlan(u.id, firstHolding.holdingId).then(() => undefined)); }}>
                            {t('recruitment.users.cancelPlan')}
                          </button>
                        )}
                        <button type="button" className="btn btn-outline" disabled={busy}
                          onClick={() => { if (window.confirm(t('recruitment.users.confirmRemove', { user: u.displayName ?? u.email }))) void guarded(() => recruiterApi.removeUser(u.id).then(() => undefined)); }}>
                          {t('recruitment.users.remove')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Change-plan modal */}
      {changeFor && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={t('recruitment.users.changeModal.title')}>
          <div className="modal-card">
            <h2>{t('recruitment.users.changeModal.title')}</h2>
            <div className="field">
              <label htmlFor="chg-plan">{t('recruitment.users.changeModal.selectPlan')}</label>
              <select id="chg-plan" value={changePlanId} onChange={(e) => setChangePlanId(e.target.value)}>
                <option value="">{t('recruitment.common.none')}</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-actions">
              <button type="button" className="btn" disabled={busy || !changePlanId}
                onClick={() => {
                  const target = changeFor;
                  const holdingId = target.plans[0]?.holdingId;
                  void guarded(() => recruiterApi.changePlan(target.id, { planId: changePlanId, holdingId }).then(() => undefined)).then(() => setChangeFor(null));
                }}>
                {t('recruitment.users.changeModal.apply')}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setChangeFor(null)}>{t('recruitment.common.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
